import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { friendlyError } from "@/lib/errors";
import { AuthLegalLinks } from "@/components/layout/AuthLegalLinks";
import {
  invitationAcceptanceMode,
  invitationErrorMessage,
  invitationLoginPath,
  invitationTokenFromSearch,
  invitationTokenLooksWellFormed,
  persistInvitationToken,
} from "@/lib/access-invitation";
import { invitationConfirmationResendAccepted } from "@/lib/confirmation-email-resend";

type InviteState =
  | { status: "closed"; reason: string }
  | { status: "checking" }
  | { status: "invalid"; reason: string }
  | { status: "register"; email: string; orgName: string | null; role: string; invitationKind?: string }
  | { status: "confirm_email"; email: string; orgName: string | null; role: string; invitationKind?: string }
  | { status: "sign_in"; email: string; orgName: string | null; role: string; invitationKind?: string };

function inviteEmail(invite: InviteState, fallback: string): string {
  if (invite.status === "register" || invite.status === "sign_in" || invite.status === "confirm_email") return invite.email;
  return fallback;
}

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = invitationTokenFromSearch(searchParams.get("invite"));

  const [invite, setInvite] = useState<InviteState>(
    invitationTokenLooksWellFormed(inviteToken) ? { status: "checking" } : { status: "closed", reason: invitationErrorMessage("MISSING") },
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [checkEmail, setCheckEmail] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    persistInvitationToken(inviteToken);
  }, [inviteToken]);

  useEffect(() => {
    if (!invitationTokenLooksWellFormed(inviteToken)) {
      setInvite({ status: "closed", reason: invitationErrorMessage("MISSING") });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error: lookupError } = await supabase.rpc("lookup_invitation", { p_token: inviteToken });
      if (cancelled) return;
      if (lookupError) {
        setInvite({ status: "invalid", reason: friendlyError(lookupError.message) });
        return;
      }
      const row = data as {
        ok?: boolean;
        code?: string;
        email?: string;
        org_name?: string;
        role?: string;
        account_exists?: boolean;
        email_confirmed?: boolean;
        account_status?: string;
        invitation_kind?: string;
      } | null;
      if (!row?.ok) {
        setInvite({ status: "invalid", reason: invitationErrorMessage(row?.code) });
        return;
      }
      const mode = invitationAcceptanceMode(row);
      setEmail(row.email ?? "");
      const next = {
        email: row.email ?? "",
        orgName: row.org_name ?? null,
        role: row.role ?? "",
        invitationKind: row.invitation_kind,
      };
      if (mode === "sign_in") {
        setInvite({ status: "sign_in", ...next });
        return;
      }
      if (mode === "confirm_email") {
        setInvite({ status: "confirm_email", ...next });
        return;
      }
      setInvite({ status: "register", ...next });
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (invite.status !== "register") return;
    setError("");
    setLoading(true);

    const { data, error: fnError } = await supabase.functions.invoke("register-with-invitation", {
      body: {
        email: invite.email,
        password,
        full_name: name,
        invite_token: inviteToken,
        action: "create",
      },
    });
    const payload = data as { success?: boolean; error?: string; code?: string; requires_confirmation?: boolean } | null;
    if (payload?.code === "ALREADY_REGISTERED") {
      persistInvitationToken(inviteToken);
      navigate(invitationLoginPath(inviteToken));
      return;
    }
    if (payload?.requires_confirmation || payload?.code === "UNCONFIRMED") {
      persistInvitationToken(inviteToken);
      setInvite({
        status: "confirm_email",
        email: invite.email,
        orgName: invite.orgName,
        role: invite.role,
        invitationKind: invite.invitationKind,
      });
      setCheckEmail(true);
      if (payload.error) setError(friendlyError(payload.error));
      setLoading(false);
      return;
    }
    if (fnError || !payload?.success) {
      setError(friendlyError(payload?.error || fnError?.message || invitationErrorMessage(payload?.code)));
      setLoading(false);
      return;
    }

    persistInvitationToken(inviteToken);
    setInvite({
      status: "confirm_email",
      email: invite.email,
      orgName: invite.orgName,
      role: invite.role,
      invitationKind: invite.invitationKind,
    });
    setCheckEmail(true);
    setLoading(false);
  };

  const handleResend = async () => {
    setError("");
    setResent(false);
    setVerifying(true);
    const { data, error: resendError } = await supabase.functions.invoke("register-with-invitation", {
      body: {
        email: inviteEmail(invite, email),
        invite_token: inviteToken,
        action: "resend",
      },
    });
    setVerifying(false);
    const payload = data as { success?: boolean; error?: string; code?: string; resent?: boolean } | null;
    if (payload?.code === "ALREADY_CONFIRMED") {
      persistInvitationToken(inviteToken);
      navigate(invitationLoginPath(inviteToken));
      return;
    }
    if (!invitationConfirmationResendAccepted(payload, resendError)) {
      setError(friendlyError(payload?.error || resendError?.message || "Unable to send confirmation email."));
      return;
    }
    persistInvitationToken(inviteToken);
    setResent(true);
  };

  const signInHref = invitationTokenLooksWellFormed(inviteToken) ? invitationLoginPath(inviteToken) : "/login";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8">
        <div className="flex items-center justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center mr-3">
            <i className="ri-stack-line text-white" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">Tradivo</h1>
        </div>

        {checkEmail || invite.status === "confirm_email" ? (
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-mail-send-line text-emerald-600 text-xl" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Confirm your email</h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              We've sent a confirmation link to <span className="font-medium text-slate-700">{inviteEmail(invite, email)}</span>.
              Click the link in that email to activate your account. The invitation stays attached until you confirm and sign in.
            </p>
            <p className="text-sm text-slate-400 mt-3">Didn't receive it? Check your spam folder.</p>
            {resent && (
              <p className="text-sm text-emerald-600 mt-3">A new confirmation email has been sent.</p>
            )}
            {error && (
              <div role="alert" className="mt-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>
            )}
            <div className="mt-4 space-y-2">
              <button
                onClick={handleResend}
                disabled={verifying}
                className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap disabled:opacity-60"
              >
                {verifying ? "Sending..." : "Resend confirmation email"}
              </button>
            </div>
            <p className="text-sm text-slate-500 mt-5">
              Already confirmed?{" "}
              <Link to={signInHref} className="text-emerald-600 hover:text-emerald-700 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        ) : invite.status === "checking" ? (
          <p className="text-sm text-slate-500 text-center">Checking invitation…</p>
        ) : invite.status === "closed" || invite.status === "invalid" ? (
          <div className="text-center">
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Invitation required</h2>
            <p className="text-sm text-slate-500 leading-relaxed">{invite.reason}</p>
            <p className="text-sm text-slate-500 mt-4">
              Tradivo accounts are created by invitation only. If you already have an account, sign in.
            </p>
            <Link
              to="/login"
              className="inline-block mt-6 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg"
            >
              Back to sign in
            </Link>
          </div>
        ) : invite.status === "sign_in" ? (
          <div className="text-center">
            <h2 className="text-lg font-semibold text-slate-800 mb-1">Sign in to accept your invitation</h2>
            <p className="text-sm text-slate-500 mb-6">
              Join {invite.orgName || "your organization"}
              {invite.role ? ` as ${invite.role}` : ""} with <strong>{invite.email}</strong>.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              This email already has a Tradivo account. Sign in to join the organisation. The invitation stays attached until you sign in.
            </p>
            <Link
              to={signInHref}
              className="inline-block px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg"
            >
              Sign in to accept
            </Link>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-slate-800 text-center mb-1">
              {invite.invitationKind === "create_organisation" ? "Start your own business" : "Accept your invitation"}
            </h2>
            <p className="text-sm text-slate-500 text-center mb-6">
              {invite.invitationKind === "create_organisation"
                ? `Create ${invite.orgName || "your organisation"} as a separate Tradivo business. You will be the owner.`
                : `Join ${invite.orgName || "your organization"}${invite.role ? ` as ${invite.role}` : ""}.`}
            </p>
            <div className="mb-4 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">
              Create your account with <strong>{invite.email}</strong>.
            </div>

            {error && (
              <div role="alert" className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="register-name" className="block text-sm font-medium text-slate-700 mb-1">Full name</label>
                <input
                  id="register-name"
                  type="text"
                  name="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  placeholder="Jane Citizen"
                />
              </div>
              <div>
                <label htmlFor="register-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  id="register-email"
                  type="email"
                  name="email"
                  required
                  value={invite.email}
                  readOnly
                  className="w-full px-3 py-2.5 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-600"
                />
              </div>
              <div>
                <label htmlFor="register-password" className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input
                  id="register-password"
                  type="password"
                  name="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-medium rounded-lg text-sm transition-colors whitespace-nowrap"
              >
                {loading ? "Creating account..." : "Create account"}
              </button>
            </form>

            <p className="text-sm text-slate-500 text-center mt-4">
              Already have an account?{" "}
              <Link to={signInHref} className="text-emerald-600 hover:text-emerald-700 font-medium">
                Sign in
              </Link>
            </p>
          </>
        )}
        <AuthLegalLinks />
      </div>
    </div>
  );
}
